/**
 * CRC-16 calculation for Protocol B
 * Polynomial: x^16 + x^15 + x^2 + 1 (0x8005)
 *
 * Characteristics:
 * - Initialized to 0
 * - For transmission: calculate CRC with two extra 0x00 bytes
 * - For reception: CRC of data + received CRC should equal 0
 */

const CRC_POLYNOMIAL = 0x8005;

/**
 * Calculate CRC-16 for Protocol B
 * @param data - Byte array to calculate CRC for
 * @returns 16-bit CRC value
 */
export function calculateCRC16(data: Uint8Array): number {
  let crc = 0x0000;

  for (let i = 0; i < data.length; i++) {
    crc ^= data[i] << 8;

    for (let bit = 0; bit < 8; bit++) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ CRC_POLYNOMIAL) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }

  return crc;
}

/**
 * Calculate CRC for transmission (adds two 0x00 bytes)
 * @param data - Data bytes to transmit
 * @returns CRC to append to frame
 */
export function calculateTransmitCRC(data: Uint8Array): number {
  const extended = new Uint8Array(data.length + 2);
  extended.set(data);
  extended[data.length] = 0x00;
  extended[data.length + 1] = 0x00;
  return calculateCRC16(extended);
}

/**
 * Verify CRC of received frame
 * @param dataWithCRC - Data bytes including the 2-byte CRC
 * @returns true if CRC is valid (result should be 0)
 */
export function verifyCRC(dataWithCRC: Uint8Array): boolean {
  return calculateCRC16(dataWithCRC) === 0x0000;
}

/**
 * Split CRC into high and low bytes
 * @param crc - 16-bit CRC value
 * @returns [highByte, lowByte]
 */
export function splitCRC(crc: number): [number, number] {
  return [(crc >> 8) & 0xFF, crc & 0xFF];
}

/**
 * Combine two bytes into CRC
 * @param high - High byte
 * @param low - Low byte
 * @returns 16-bit CRC value
 */
export function combineCRC(high: number, low: number): number {
  return ((high & 0xFF) << 8) | (low & 0xFF);
}
